import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import WorkIcon from '@mui/icons-material/Work';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import AirIcon from '@mui/icons-material/Air';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WaterIcon from '@mui/icons-material/Water';
import FactoryIcon from '@mui/icons-material/Factory';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import CategoryIcon from '@mui/icons-material/Category';
import PortfolioDetail from './PortfolioDetail';
import BenchmarkStep from './BenchmarkStep';
import KpiCard, { fmtAUDCompact, fmtMWhCompact, fmtPct, fmtAUDPerMWh } from './KpiCard';
import { useThemeColors } from '../theme/ThemeModeContext';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:5001';

const FUEL_COLORS = {
  Solar: '#f59e0b',
  Wind: '#3b82f6',
  Gas: '#ef4444',
  Hydro: '#06b6d4',
  'Black Coal': '#374151',
  'Brown Coal': '#78350f',
  Battery: '#8b5cf6',
  Other: '#6b7280',
};

const FUEL_ICONS = {
  Solar: WbSunnyIcon,
  Wind: AirIcon,
  Gas: LocalFireDepartmentIcon,
  Hydro: WaterIcon,
  'Black Coal': FactoryIcon,
  'Brown Coal': FactoryIcon,
  Battery: BatteryChargingFullIcon,
  Other: CategoryIcon,
};

const FuelIcon = ({ fuelCode, size = 14, sx = {} }) => {
  const Icon = FUEL_ICONS[fuelCode] || FUEL_ICONS.Other;
  const color = FUEL_COLORS[fuelCode] || FUEL_COLORS.Other;
  return <Icon sx={{ fontSize: size, color, ...sx }} />;
};

const REGION_LABELS = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  SA: 'South Australia',
  TAS: 'Tasmania',
  NSW1: 'New South Wales',
  VIC1: 'Victoria',
  QLD1: 'Queensland',
  SA1: 'South Australia',
  TAS1: 'Tasmania',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '\u2014';
  return new Date(dateStr).toLocaleDateString('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const PortfoliosDashboard = () => {
  const { portfolioId } = useParams();
  const navigate = useNavigate();
  const colors = useThemeColors();
  const [portfolios, setPortfolios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState(null);

  // Create wizard state
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createStep, setCreateStep] = useState(0); // 0 = name+assets, 1 = benchmarks
  const [createName, setCreateName] = useState('');
  const [allAssets, setAllAssets] = useState([]);
  const [selectedAssets, setSelectedAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  // benchmarks: { [asset_id]: [{...assetObj, source: 'auto'|'user', cross_region?: bool}] }
  const [benchmarks, setBenchmarks] = useState({});

  const [fleet, setFleet] = useState(null);
  const [alertsCount, setAlertsCount] = useState(null);

  const fetchPortfolios = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/portfolios`);
      setPortfolios(response.data.portfolios || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load portfolios');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFleet = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/portfolios/fleet/overview`,
        { params: { period: 'mtd' } });
      setFleet(r.data);
    } catch { /* fleet KPIs are optional — silent fail */ }
  }, []);

  const fetchAlertsCount = useCallback(async () => {
    try {
      const r = await axios.get(`${API_BASE_URL}/api/portfolios/alerts`,
        { params: { period: '7d' } });
      setAlertsCount(r.data?.counts?.all ?? 0);
    } catch { /* */ }
  }, []);

  useEffect(() => {
    fetchPortfolios();
    fetchFleet();
    fetchAlertsCount();
  }, [fetchPortfolios, fetchFleet, fetchAlertsCount]);

  const handleViewPortfolio = useCallback(
    (id) => {
      navigate(`/portfolios/${id}`);
    },
    [navigate]
  );

  const handleDeleteClick = useCallback((e, portfolio) => {
    e.stopPropagation();
    setPortfolioToDelete(portfolio);
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (!portfolioToDelete) return;
    try {
      await axios.delete(`${API_BASE_URL}/api/portfolios/${portfolioToDelete.id}`);
      setPortfolios((prev) => prev.filter((p) => p.id !== portfolioToDelete.id));
    } catch (err) {
      setError('Failed to delete portfolio');
    } finally {
      setDeleteDialogOpen(false);
      setPortfolioToDelete(null);
    }
  }, [portfolioToDelete]);

  const handleDeleteFromDetail = useCallback(
    async (deletedId) => {
      try {
        await axios.delete(`${API_BASE_URL}/api/portfolios/${deletedId}`);
        setPortfolios((prev) => prev.filter((p) => p.id !== deletedId));
        navigate('/portfolios');
      } catch (err) {
        setError('Failed to delete portfolio');
      }
    },
    [navigate]
  );

  const handleOpenCreate = async () => {
    setCreateDialogOpen(true);
    setCreateStep(0);
    setCreateName('');
    setSelectedAssets([]);
    setBenchmarks({});
    setAssetsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/portfolios/assets`);
      setAllAssets(response.data.assets || []);
    } catch (err) {
      setError('Failed to load assets');
    } finally {
      setAssetsLoading(false);
    }
  };

  // When a user removes an asset on Step 1 after visiting Step 2, drop its
  // benchmarks so we don't ship stale entries to the server.
  useEffect(() => {
    const keep = new Set(selectedAssets.map((a) => a.asset_id));
    setBenchmarks((prev) => {
      const next = {};
      let changed = false;
      Object.entries(prev).forEach(([aid, list]) => {
        if (keep.has(aid)) next[aid] = list;
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [selectedAssets]);

  const handleCreate = async () => {
    if (!createName.trim() || selectedAssets.length === 0) return;
    try {
      setCreating(true);
      // Flatten benchmark state into parallel id/source maps for the API.
      const benchmarkPayload = {};
      const benchmarkSourcesPayload = {};
      Object.entries(benchmarks).forEach(([aid, list]) => {
        if (!list || list.length === 0) return;
        benchmarkPayload[aid] = list.map((b) => b.asset_id);
        benchmarkSourcesPayload[aid] = list.map((b) => b.source || 'auto');
      });
      const response = await axios.post(`${API_BASE_URL}/api/portfolios`, {
        name: createName.trim(),
        asset_ids: selectedAssets.map((a) => a.asset_id),
        benchmarks: benchmarkPayload,
        benchmark_sources: benchmarkSourcesPayload,
      });
      setCreateDialogOpen(false);
      // Navigate to the new portfolio
      navigate(`/portfolios/${response.data.id}`);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to create portfolio');
    } finally {
      setCreating(false);
    }
  };

  const step0Valid = createName.trim().length > 0 && selectedAssets.length > 0;

  // Detail View
  if (portfolioId) {
    return (
      <PortfolioDetail
        portfolioId={portfolioId}
        onDelete={handleDeleteFromDetail}
      />
    );
  }

  // List View
  return (
    <Box>
      {/* Header */}
      <Box className="animate-in" sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <WorkIcon sx={{ fontSize: 26, color: 'primary.main' }} />
        <Typography
          variant="h5"
          sx={{ color: 'text.primary', fontWeight: 700, letterSpacing: '-0.02em' }}
        >
          Portfolios
        </Typography>
        <Chip
          label={`${portfolios.length} portfolio${portfolios.length !== 1 ? 's' : ''}`}
          size="small"
          variant="outlined"
          sx={{
            color: 'text.secondary',
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: '0.72rem',
            fontWeight: 500,
          }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
          sx={{
            fontWeight: 600,
            px: 2.5,
            py: 0.8,
          }}
        >
          Create Portfolio
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, border: '1px solid rgba(229, 56, 79, 0.2)' }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Loading */}
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress sx={{ color: 'primary.main' }} />
        </Box>
      )}

      {/* Fleet KPI strip — Cluster 1.2 (Fleet Home) */}
      {fleet && (
        <Box sx={{
          display: 'grid', gap: 1.5, mb: 3,
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
        }}>
          {/* Fleet KPI strip — all four tiles carry a left rule. Signal green
              marks the headline metric (revenue); danger red flips on when
              alerts > 0; the rest sit on a neutral stone rule so the strip
              reads as one consistent block. */}
          <KpiCard
            label={`Fleet revenue · ${fleet.period?.label?.split(' ')[0] || 'MTD'}`}
            value={fmtAUDCompact(fleet.kpis?.revenue_aud)}
            accent={colors.SIGNAL}
          />
          <KpiCard
            label="Fleet generation"
            value={fmtMWhCompact(fleet.kpis?.generation_mwh)}
            accent={colors.STONE}
          />
          <KpiCard
            label="Weighted capture"
            value={fmtAUDPerMWh(fleet.kpis?.weighted_capture_aud_per_mwh)}
            unit="/MWh"
            accent={colors.STONE}
          />
          <KpiCard
            label="Active alerts"
            value={alertsCount ?? '—'}
            unit={alertsCount != null ? (alertsCount === 1 ? 'alert' : 'alerts') : ''}
            accent={alertsCount > 0 ? colors.DANGER : colors.STONE}
          />
        </Box>
      )}

      {/* Portfolio card grid */}
      {!loading && portfolios.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: '1fr 1fr 1fr' },
          }}
        >
          {portfolios.map((portfolio, index) => (
            <Paper
              key={portfolio.id}
              onClick={() => handleViewPortfolio(portfolio.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleViewPortfolio(portfolio.id);
              }}
              sx={{
                position: 'relative',
                p: 3,
                cursor: 'pointer',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 3,
                boxShadow: 'none',
                animation: 'fadeInUp 0.4s ease-out both',
                animationDelay: `${index * 0.04}s`,
                transition: 'border-color 0.15s ease, transform 0.15s ease, background-color 0.15s ease',
                '&:hover': {
                  borderColor: 'primary.main',
                  bgcolor: 'rgba(217, 138, 43, 0.04)',
                  transform: 'translateY(-2px)',
                  '& .pf-delete': { opacity: 1 },
                },
                '&:focus-visible': {
                  outline: '2px solid',
                  outlineColor: 'primary.main',
                  outlineOffset: 2,
                },
              }}
            >
              <Tooltip title="Delete">
                <IconButton
                  className="pf-delete"
                  size="small"
                  onClick={(e) => handleDeleteClick(e, portfolio)}
                  sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    opacity: 0,
                    color: 'text.secondary',
                    transition: 'opacity 0.15s ease, color 0.15s ease',
                    '&:hover': { color: 'error.main' },
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'rgba(217, 138, 43, 0.10)',
                }}
              >
                <WorkIcon sx={{ fontSize: 24, color: 'primary.main' }} />
              </Box>

              <Typography
                sx={{
                  fontSize: '1.05rem',
                  fontWeight: 700,
                  color: 'text.primary',
                  letterSpacing: '-0.015em',
                  mb: 1,
                  pr: 3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {portfolio.name}
              </Typography>

              <Chip
                label={`${portfolio.asset_count || 0} asset${(portfolio.asset_count || 0) !== 1 ? 's' : ''}`}
                size="small"
                sx={{
                  bgcolor: 'rgba(217, 138, 43, 0.10)',
                  color: 'primary.main',
                  fontWeight: 600,
                  fontSize: '0.72rem',
                  mb: 2,
                }}
              />

              <Box sx={{ display: 'flex', gap: 4 }}>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.66rem',
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      mb: 0.25,
                    }}
                  >
                    Created
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                    {formatDate(portfolio.created_at)}
                  </Typography>
                </Box>
                <Box>
                  <Typography
                    sx={{
                      fontSize: '0.66rem',
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.07em',
                      mb: 0.25,
                    }}
                  >
                    Updated
                  </Typography>
                  <Typography sx={{ fontSize: '0.82rem', color: 'text.primary' }}>
                    {formatDate(portfolio.updated_at)}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {/* Empty State */}
      {!loading && portfolios.length === 0 && (
        <Paper
          className="animate-in stagger-2"
          sx={{
            p: 5,
            textAlign: 'center',
            bgcolor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <WorkIcon
            sx={{
              fontSize: 56,
              color: 'text.secondary',
              mb: 1.5,
              opacity: 0.3,
            }}
          />
          <Typography
            variant="body1"
            sx={{ color: 'text.secondary', fontWeight: 500 }}
          >
            No portfolios created yet.
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.secondary', mt: 0.5, opacity: 0.7 }}
          >
            Click "Create Portfolio" to group your NEM assets.
          </Typography>
        </Paper>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Portfolio</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'text.secondary' }}>
            Are you sure you want to delete "{portfolioToDelete?.name}"? This
            will remove the portfolio and all its asset associations. This
            action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Portfolio Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: 'background.paper',
            border: '1px solid rgba(255, 255, 255, 0.08)',
          },
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Create Portfolio</DialogTitle>
        <DialogContent>
          <Stepper activeStep={createStep} sx={{ mb: 3, mt: 1 }}>
            <Step>
              <StepLabel>Name & assets</StepLabel>
            </Step>
            <Step>
              <StepLabel>Benchmarks</StepLabel>
            </Step>
          </Stepper>

          {createStep === 0 && (
            <Box>
              <TextField
                autoFocus
                fullWidth
                label="Portfolio Name"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                placeholder="e.g., Solar Assets NSW"
                sx={{ mt: 1, mb: 3 }}
              />

              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: 'text.secondary',
                  textTransform: 'uppercase',
                  letterSpacing: '0.06em',
                  mb: 1.5,
                }}
              >
                Select Assets
              </Typography>

              {assetsLoading ? (
                <Box display="flex" justifyContent="center" p={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <Autocomplete
                  multiple
                  options={allAssets}
                  value={selectedAssets}
                  onChange={(_, newValue) => setSelectedAssets(newValue)}
                  getOptionLabel={(option) =>
                    `${option.name} (${option.asset_id})`
                  }
                  groupBy={(option) =>
                    REGION_LABELS[option.region_id] || option.region_id
                  }
                  isOptionEqualToValue={(option, value) =>
                    option.asset_id === value.asset_id
                  }
                  renderOption={(props, option) => {
                    const { key, ...otherProps } = props;
                    return (
                      <li key={key} {...otherProps}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1.5,
                            width: '100%',
                          }}
                        >
                          <FuelIcon fuelCode={option.fuel_code} size={16} sx={{ flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            sx={{ fontWeight: 500, flex: 1 }}
                          >
                            {option.name}
                          </Typography>
                          <Chip
                            label={option.asset_id}
                            size="small"
                            sx={{
                              fontFamily: "'JetBrains Mono', monospace",
                              fontSize: '0.68rem',
                              height: 22,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                              fontFamily: "'JetBrains Mono', monospace",
                            }}
                          >
                            {option.capacity != null ? Number(option.capacity).toFixed(1) : '\u2014'} MW
                          </Typography>
                        </Box>
                      </li>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search assets by name or DUID"
                      placeholder="Type to search..."
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...chipProps } = getTagProps({ index });
                      return (
                        <Chip
                          key={key}
                          label={`${option.name} (${option.asset_id})`}
                          size="small"
                          {...chipProps}
                          sx={{
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            bgcolor: `${
                              FUEL_COLORS[option.fuel_code] || FUEL_COLORS.Other
                            }12`,
                            border: `1px solid ${
                              FUEL_COLORS[option.fuel_code] || FUEL_COLORS.Other
                            }30`,
                          }}
                        />
                      );
                    })
                  }
                  disableCloseOnSelect
                />
              )}

              {selectedAssets.length > 0 && (
                <Typography
                  variant="body2"
                  sx={{ mt: 2, color: 'text.secondary' }}
                >
                  {selectedAssets.length} asset
                  {selectedAssets.length !== 1 ? 's' : ''} selected
                  {' \u2022 '}
                  {selectedAssets
                    .reduce((sum, a) => sum + (Number(a.capacity) || 0), 0)
                    .toFixed(1)}{' '}
                  MW total capacity
                </Typography>
              )}
            </Box>
          )}

          {createStep === 1 && (
            <BenchmarkStep
              selectedAssets={selectedAssets}
              allAssets={allAssets}
              benchmarks={benchmarks}
              setBenchmarks={setBenchmarks}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Box sx={{ flex: 1 }} />
          {createStep === 1 && (
            <Button onClick={() => setCreateStep(0)} disabled={creating}>
              Back
            </Button>
          )}
          {createStep === 0 && (
            <Button
              onClick={() => setCreateStep(1)}
              variant="contained"
              disabled={!step0Valid}
            >
              Next
            </Button>
          )}
          {createStep === 1 && (
            <Button
              onClick={handleCreate}
              variant="contained"
              disabled={creating || !step0Valid}
            >
              {creating ? (
                <CircularProgress size={20} sx={{ color: '#F4F2EC' }} />
              ) : (
                'Create Portfolio'
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortfoliosDashboard;
